import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ChangeContributorLocationOptions,
  ChangeContributorLocationResponse,
  ChangeContributorRoleResponse,
  ChangeRoleOptions,
  ContributorCreatedEnvelope,
  GetContributorsQuotaResponse,
  GetSiteContributorsV2Options,
  GetSiteContributorsV2Response,
  QuerySiteContributorsOptions,
  QuerySiteContributorsResponse,
  changeContributorLocation as universalChangeContributorLocation,
  changeRole as universalChangeRole,
  getContributorsQuota as universalGetContributorsQuota,
  getSiteContributorsV2 as universalGetSiteContributorsV2,
  querySiteContributors as universalQuerySiteContributors,
  removeContributor as universalRemoveContributor,
} from './identity-v1-contributor-contributors.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/user-management' };

/** @internal */
export function getSiteContributorsV2(
  httpClient: HttpClient
): GetSiteContributorsV2Signature {
  return (options?: GetSiteContributorsV2Options) =>
    universalGetSiteContributorsV2(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSiteContributorsV2Signature {
  /**
   * returns a list of all the contributors + invites to be contributors of a given meta site ID taken from the
   * context.
   */
  (options?: GetSiteContributorsV2Options): Promise<
    NonNullablePaths<
      GetSiteContributorsV2Response,
      | `contributors`
      | `contributors.${number}.metaData._id`
      | `contributors.${number}._id`
      | `invites`
      | `invites.${number}._id`
      | `invites.${number}.siteId`
      | `invites.${number}.email`
      | `invites.${number}.inviterId`
      | `invites.${number}.status`
      | `invites.${number}.acceptLink`
      | `invites.${number}.inviterAccountId`
      | `contributorsQuota.limitedOptions.limit`
      | `contributorsQuota.limitedOptions.used`
      | `contributorsQuota.type`
      | `pendingOwner.email`
      | `pendingOwner.acceptLink`
      | `enrichedInvites`
      | `enrichedInvites.${number}._id`
      | `enrichedInvites.${number}.email`
      | `enrichedInvites.${number}.acceptLink`
      | `enrichedInvites.${number}.inviterId`
      | `enrichedInvites.${number}.siteId`
      | `enrichedInvites.${number}.status`,
      5
    >
  >;
}

export function changeRole(httpClient: HttpClient): ChangeRoleSignature {
  return (
    accountId: string,
    options: NonNullablePaths<ChangeRoleOptions, `newRoles`, 2>
  ) =>
    universalChangeRole(
      accountId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ChangeRoleSignature {
  /**
   * Updates all of a contributor's roles for the specified site, overriding any existing roles.
   *
   * Replaces all existing role assignments for the contributor with the roles specified in `newRoles`. To retrieve available roles, call [Get Roles Info](https://dev.wix.com/docs/api-reference/account-level/user-management/accounts/users/get-roles-info).
   * @param - Contributor's account ID.
   * @param - Filter options. The `newRoles` field **must** be passed.
   */
  (
    accountId: string,
    options: NonNullablePaths<ChangeRoleOptions, `newRoles`, 2>
  ): Promise<
    NonNullablePaths<
      ChangeContributorRoleResponse,
      | `newAssignedRoles`
      | `newAssignedRoles.${number}.roleId`
      | `newAssignedRoles.${number}.assignmentId`,
      4
    >
  >;
}

export function changeContributorLocation(
  httpClient: HttpClient
): ChangeContributorLocationSignature {
  return (
    accountId: string,
    options: NonNullablePaths<
      ChangeContributorLocationOptions,
      `newLocations`,
      2
    >
  ) =>
    universalChangeContributorLocation(
      accountId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ChangeContributorLocationSignature {
  /**
   * Updates the business locations within all a contributor's role assignments for the specified site, overriding any existing locations.
   *
   * Replaces all existing location assignments for the contributor's roles with the locations specified in `newLocations`. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
   * @param - Contributor's account ID.
   */
  (
    accountId: string,
    options: NonNullablePaths<
      ChangeContributorLocationOptions,
      `newLocations`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ChangeContributorLocationResponse,
      `newAssignedLocations`,
      2
    >
  >;
}

export function querySiteContributors(
  httpClient: HttpClient
): QuerySiteContributorsSignature {
  return (options?: QuerySiteContributorsOptions) =>
    universalQuerySiteContributors(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QuerySiteContributorsSignature {
  /**
   * Retrieves a list of contributors for the specified site, given the provided filters.
   *
   * Returns contributor account IDs and account owner IDs that match the specified filters.
   * @param - Filter options.
   */
  (options?: QuerySiteContributorsOptions): Promise<
    NonNullablePaths<QuerySiteContributorsResponse, `contributors`, 2>
  >;
}

/** @internal */
export function getContributorsQuota(
  httpClient: HttpClient
): GetContributorsQuotaSignature {
  return () =>
    universalGetContributorsQuota(
      // @ts-ignore
      { httpClient }
    );
}

interface GetContributorsQuotaSignature {
  /**
   * returns the quota information for contributors on the given site.
   */
  (): Promise<
    NonNullablePaths<
      GetContributorsQuotaResponse,
      | `contributorsQuota.limitedOptions.limit`
      | `contributorsQuota.limitedOptions.used`
      | `contributorsQuota.type`,
      4
    >
  >;
}

export function removeContributor(
  httpClient: HttpClient
): RemoveContributorSignature {
  return (accountId: string) =>
    universalRemoveContributor(
      accountId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveContributorSignature {
  /**
   * Removes a contributor from the site by clearing all their role assignments.
   * @param - Contributor's account ID to remove from the site.
   */
  (accountId: string): Promise<void>;
}

export const onContributorCreated = EventDefinition(
  'wix.identity.v1.contributor_created',
  true,
  (event: ContributorCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'entity.joinedAt' }, { path: 'metadata.eventTime' }],
        },
      ])
    )
)<ContributorCreatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApiAccountInfo,
  AppInvite,
  AssignedPolicy,
  Assignment,
  BaseEventMetadata,
  BulkGetUserRolesOnSiteRequest,
  BulkGetUserRolesOnSiteResponse,
  BulkValidateEmailInviteEligibilityRequest,
  BulkValidateEmailInviteEligibilityResponse,
  ChangeContributorLocationOptions,
  ChangeContributorLocationRequest,
  ChangeContributorLocationResponse,
  ChangeContributorRoleRequest,
  ChangeContributorRoleResponse,
  ChangeRoleOptions,
  CompanionResource,
  Condition,
  ConditionAttributeType,
  Conditions,
  Contributor,
  ContributorCreatedEnvelope,
  ContributorLimit,
  ContributorV2,
  ContributorsQuota,
  ContributorsQuotaOptionsOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  EmailInviteEligibility,
  EnrichedSiteInvite,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FieldSet,
  GetAppContributorsRequest,
  GetAppContributorsResponse,
  GetContributorsQuotaRequest,
  GetContributorsQuotaResponse,
  GetCurrentUserRolesRequest,
  GetCurrentUserRolesResponse,
  GetSiteContributorsRequest,
  GetSiteContributorsResponse,
  GetSiteContributorsV2Options,
  GetSiteContributorsV2Request,
  GetSiteContributorsV2Response,
  HandleSiteTransferRequest,
  HandleSiteTransferResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InviteStatus,
  LimitedOptions,
  LocalizedPolicyInfo,
  LocalizedRole,
  LocationsRestriction,
  MessageEnvelope,
  Name,
  PendingOwner,
  PersonMetaData,
  Policy,
  PredefinedRole,
  PredefinedRoles,
  QuerySiteContributorsFilter,
  QuerySiteContributorsOptions,
  QuerySiteContributorsRequest,
  QuerySiteContributorsResponse,
  RemoveContributorRequest,
  RemoveContributorResponse,
  Resource,
  ResourceType,
  RestoreInfo,
  Restriction,
  RestrictionRestrictionsOneOf,
  Role,
  SiteInvite,
  SiteLocationAssignment,
  SiteRestriction,
  SiteRoleAssignment,
  Subject,
  SubjectContext,
  SubjectContextType,
  SubjectType,
  Team,
  Type,
  User,
  UserLocalizedRoles,
  UserSubject,
  WebhookIdentityType,
} from './identity-v1-contributor-contributors.universal.js';
